import React from "react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { QuietHoursBlockedError } from "@/model/interface/Compliance";

interface QuietHoursWarningDialogProps {
  open: boolean;
  error: QuietHoursBlockedError | null;
  actionLabel: string;
  onProceed: () => void;
  onCancel: () => void;
}

export default function QuietHoursWarningDialog({
  open,
  error,
  actionLabel,
  onProceed,
  onCancel,
}: QuietHoursWarningDialogProps) {
  const recipients = error?.blocked ?? [];
  const overridable = error?.overridable ?? false;
  const isCapOnly = recipients.length > 0 && recipients.every((recipient) => recipient.reason === "CAPPED");
  const title = isCapOnly
    ? "Daily call limit reached"
    : overridable
      ? "Outside calling hours"
      : "Contact not allowed right now";

  return (
    <AlertDialog
      open={open}
      onOpenChange={(value) => {
        if (!value) {
          onCancel();
        }
      }}
    >
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>{title}</AlertDialogTitle>
          <AlertDialogDescription>
            {error?.detail ??
              "This recipient is outside the hours they can be contacted."}
          </AlertDialogDescription>
        </AlertDialogHeader>

        {recipients.length > 0 && (
          <ul className="space-y-1 rounded-md border bg-muted/40 p-3 text-sm">
            {recipients.map((recipient) => (
              <li key={recipient.phoneNumber} className="flex justify-between gap-4">
                <span className="font-medium">{recipient.phoneNumber}</span>
                <span className="text-muted-foreground">
                  {recipient.reason === "UNKNOWN"
                    ? "local time unknown"
                    : [recipient.calleeLocalTime, recipient.regionName ?? recipient.calleeTimezone]
                        .filter(Boolean)
                        .join(" · ")}
                </span>
              </li>
            ))}
          </ul>
        )}

        <AlertDialogFooter>
          <AlertDialogCancel onClick={onCancel}>
            {overridable ? "Cancel" : "Close"}
          </AlertDialogCancel>
          {overridable && (
            <AlertDialogAction onClick={onProceed}>{actionLabel}</AlertDialogAction>
          )}
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
